import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import Table, { ExtendedColumnType } from "components/Table";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";
import {
    createGstrPromiseClient,
    getGstrRecords,
    exportGstrtaxdeductor,
} from "api/services/clients/gstr-clients";
import { snack } from "components/toast";
import React from "react";
import {
    RegularTaxHeadings,
    SortDirection,
} from "data/constants";
import { getGstFinancialYears } from "utils/FinancialYears";
import { useSelector } from "react-redux";
import { selectGstRegister } from "redux/reducers/gstRegisterSlice";
import PeriodSelect from "views/register/GstReturns/YearmonthPeriodSelect";
import AppliedFilters from "views/register/ApplidedFilters";
import sortHandler from "components/Table/SortHandler";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import GstReturnsTracker from "views/register/Filter";
import excelIcon from "./../../../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import { GST_STATUS_STYLE, getDueDate, resolveGstStatus } from "utils/gstStatus";
import { useSelectionSync } from "views/register/GstReturns/design/ToolbarSlot";
import { removeGstrRegistrations } from "api/services/clients/gstr-clients";
import { useConfirm } from "context/ConfirmDialog";

const RTNTYPE = "GSTR7";
const REG_TYPE = "tax_deductor";

function Gstr7Task() {
    const financialYears = getGstFinancialYears();
    const confirm = useConfirm();
    const [selected, setSelected] = useState<any[]>([]);
    const currentMonth = new Date().getMonth();
    const adjustedMonthIndex = currentMonth === 0 ? 12 : currentMonth;
    const reqMonth =
        adjustedMonthIndex < 10 ? `0${adjustedMonthIndex}` : `${adjustedMonthIndex}`;
    const [selectedYear, setSelectedYear] = React.useState(financialYears[0]);
    const [selectedMonth, setSelectedMonth] = React.useState(reqMonth);
    const [page, setPage] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(10);
    const [filters, setFilters] = useState({
        labels: [],
        search: "",
    });
    const [lodd, setLoad] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);
    const queryClient = useQueryClient();
    const states = useSelector(selectGstRegister);
    const [sort, setSort] = useState<any>({});
    const [openFilter, setOpenFilter] = useState<boolean>(false);

    useEffect(() => {
        setSelected([]);
    }, [selectedYear, selectedMonth]);

    const dueDate = useMemo(() => getDueDate(RTNTYPE, selectedYear, selectedMonth, "month"), [selectedYear, selectedMonth]);

    const monthLydefaultColumns: Array<ExtendedColumnType> = [
        {
            key: "client.clientId",
            title: "Client ID",
            render: (row: any) =>
                row?.client ? row?.client?.clientId : row?.clientGroup?.clientId,
        },
        {
            key: "category",
            title: "Category",
            render: (row: any) => row?.client?.newCategory?.name,
        },
        {
            key: "client.gstNumber",
            title: "GSTIN",
            default: true,
            render: (row: any) =>
                row?.client ? row?.client?.gstNumber : row?.clientGroup?.gstNumber,
        },
        {
            key: "client.displayName",
            title: "Client Name",
            default: true,
            render: (row: any) =>
                row?.client
                    ? row?.client?.displayName
                    : row?.clientGroup?.displayName,
        },
        {
            key: "status",
            title: "Return Status",
            sort: true,
            active: false,
            direction: SortDirection.ASC,
            default: true,
            render: (row: any) => {
                const s = GST_STATUS_STYLE[resolveGstStatus(row, dueDate)];
                return <Typography sx={{ color: s.ink }}>{s.label}</Typography>;
            },
        },
        {
            key: "dof",
            title: "Date of Filing",
            sort: true,
            active: false,
            direction: SortDirection.ASC,
            default: true,
            render: (row: any) =>
                row?.returnsData?.[0]?.dof ? row?.returnsData?.[0]?.dof : "-",
        },
        {
            key: "arn",
            title: "ARN",
            default: true,
            render: (row: any) =>
                row?.returnsData?.[0]?.arn ? row?.returnsData?.[0]?.arn : "-",
        },
    ];

    const { data, isLoading }: ResType = useQuery(
        [
            "clients-gst-returns",
            {
                limit: pageCount,
                offset: page * pageCount,
                query: {
                    ...filters,
                    labels: filters.labels.map((c: any) => c?.id),
                    sort,
                },
                type: REG_TYPE,
                rtntype: RTNTYPE,
                selectedYear: selectedYear,
                category:
                    states?.appliedFilters?.category["value"] === undefined
                        ? []
                        : [states?.appliedFilters?.category["value"]],
                subCategory: states?.appliedFilters?.subCategory.map((c: any) => c?.value),
                status: (states?.appliedFilters?.status || []).map((s: any) => s.value),
                jurisdiction:
                    states?.appliedFilters?.jurisdiction["value"] === undefined
                        ? []
                        : [states?.appliedFilters?.jurisdiction["value"]],
            },
        ],
        getGstrRecords,
        {
            enabled: !!selectedYear && !!selectedMonth,
            onError: (err: any) => {
                if (!err?.error) {
                    snack.error(`An error occurred while fetching register data`);
                }
            },
        }
    );

    // The backend now always returns every period of the FY in one call - group
    // it into one entry per client (keeping every period's row), then pick out
    // just the selected month's row per client for this flat, single-month table.
    const clientsByKey = useMemo(() => {
        const map = new Map<string, { key: string; anyRow: any; rowsByPeriod: Map<string, any> }>();
        (data?.data?.result || []).forEach((row: any) => {
            const key = row.client ? `c-${row.client.id}` : `g-${row.clientGroup?.id}`;
            if (!map.has(key)) map.set(key, { key, anyRow: row, rowsByPeriod: new Map() });
            map.get(key)!.rowsByPeriod.set(row.periodValue, row);
        });
        return map;
    }, [data]);

    const tableData = useMemo(
        () => Array.from(clientsByKey.values()).map((c) => c.rowsByPeriod.get(selectedMonth)).filter(Boolean),
        [clientsByKey, selectedMonth],
    );

    const [columns, setColumns] = useState(_.cloneDeep(monthLydefaultColumns));
    const handleSort = (v: any) => {
        sortHandler({
            key: v.key,
            columns,
            sortState: sort,
            setSortState: setSort,
            setColumns: setColumns,
        });
    };

    const totalCount = data?.data?.count;

    const handleExport = async () => {
        try {
            setExporting(true);

            const exportFiltersPayload = {
                limit: pageCount,
                offset: page * pageCount,
                query: {
                    ...filters,
                    labels: filters.labels.map((c: any) => c?.id),
                    sort,
                },
                type: REG_TYPE,
                rtntype: RTNTYPE,
                selectedYear,
                category:
                    states?.appliedFilters?.category["value"] === undefined
                        ? []
                        : [states?.appliedFilters?.category["value"]],
                subCategory: states?.appliedFilters?.subCategory.map((c: any) => c?.value),
                status: (states?.appliedFilters?.status || []).map((s: any) => s.value),
                jurisdiction:
                    states?.appliedFilters?.jurisdiction["value"] === undefined
                        ? []
                        : [states?.appliedFilters?.jurisdiction["value"]],
            };

            const res = await exportGstrtaxdeductor(exportFiltersPayload);
            const arr = new Uint8Array(res.data?.buffer?.data);
            const blob = new Blob([arr], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const file = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = file;
            link.download = res.data?.fileName || "GSTR-7-Report.xlsx";
            link.click();
        } catch (err: any) {
            snack.error("Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    const { mutate, isLoading: loading } = useMutation(createGstrPromiseClient, {
        onSuccess: (res) => {
            setTimeout(() => {
                queryClient.invalidateQueries("clients-gst-returns");
                setLoad(false);
                snack.success("Sync Successful");
            }, 200);
        },
        onError: (err: any) => {
            snack.error(err.response.data.message);
            setLoad(false);
        },
    });

    const getGstReturnClients = async () => {
        if (!selectedYear) return snack.error("Select Financial Year");
        if (!selectedMonth) return snack.error("Select Month");
        if (!selected.length) return snack.error("Select At least one Client");
        let clientDetails: any = [];
        for (let i of selected) {
            // Only sync clients that have a genuine "Not Filed" month somewhere
            // this FY - Filed, NA, a gap, or Not-due-yet all mean there's
            // nothing to gain from syncing.
            const key = i?.client ? `c-${i.client.id}` : `g-${i?.clientGroup?.id}`;
            const rowsByPeriod = clientsByKey.get(key)?.rowsByPeriod;
            const hasNotFiled = rowsByPeriod
                ? Array.from(rowsByPeriod.values()).some((row: any) => row?.returnsData?.[0]?.status === "Not Filed")
                : false;
            if (!hasNotFiled) continue;
            if (i?.client?.id) {
                clientDetails.push({
                    id: i.id,
                    clientId: i.client.id,
                    gstNumber: i.client.gstNumber,
                    returnsData: i.returnsData,
                });
            } else if (i?.clientGroup?.id) {
                clientDetails.push({
                    id: i.id,
                    clientGroupId: i.clientGroup.id,
                    gstNumber: i.clientGroup.gstNumber,
                    returnsData: i.returnsData,
                });
            }
        }
        if (!clientDetails.length) {
            return snack.error(
                "One-Time Sync! Your GST Returns data has been synced and is ready to use."
            );
        }
        setLoad(true);

        mutate({
            financialYear: selectedYear,
            type: REG_TYPE,
            clientDetails,
            rtntype: RTNTYPE,
        });
    };

    const { mutate: removeMutate, isLoading: removing } = useMutation(
        () => removeGstrRegistrations(selected.map((r: any) => r.id)),
        {
            onSuccess: () => {
                snack.success("Removed from tracking");
                setSelected([]);
                queryClient.invalidateQueries("clients-gst-returns");
            },
            onError: (err: any) => snack.error(err?.response?.data?.message || "Failed to remove from tracking"),
        }
    );

    const handleRemove = () => {
        confirm({
            msg: `Would you like to remove these Client(s) from GST Return Tracking? Their filing history will remain intact, but they will no longer appear on this list. You can re-add them later if needed...`,
            action: () => removeMutate(),
        });
    };

    useSelectionSync({ count: selected.length, onRemove: handleRemove, removing });

    return (
        <Box>
            <Grid
                container
                spacing={2}
                mb={2}
                alignItems="center"
                justifyContent="space-between"
            >
                {/* Period Select + Search */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    order={{ xs: 2, md: 1 }}
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={2}
                    justifyContent={{ xs: "center", md: "flex-start" }}
                >
                    <PeriodSelect
                        selectedYear={selectedYear}
                        setSelectedYear={setSelectedYear}
                        selectedMonth={selectedMonth}
                        setSelectedMonth={setSelectedMonth}
                        searchValue={filters.search}
                        onSearchChange={(v) => setFilters({ ...filters, search: v })}
                    />
                </Grid>

                {/* Buttons Section */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    order={{ xs: 1, md: 2 }}
                >
                    <Box
                        display="flex"
                        justifyContent={{ xs: "center", md: "flex-end" }}
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => getGstReturnClients()}
                            disabled={loading || lodd}
                        >
                            {loading || lodd ? "Syncing…" : "Sync With Portal"}
                        </Button>

                        <Button
                            startIcon={<FilterAltOutlinedIcon />}
                            onClick={() => setOpenFilter(true)}
                            color="primary"
                            sx={{
                                border: "1px solid lightgrey",
                                borderRadius: "4px",
                            }}
                        >
                            Filters
                        </Button>

                        <Tooltip title={exporting ? 'Exporting...' : 'Data Export to Excel'}>
                            <Box
                                component="img"
                                src={excelIcon}
                                alt="Export"
                                sx={{
                                    cursor: exporting ? 'not-allowed' : 'pointer',
                                    width: 33,
                                    height: 33,
                                    opacity: exporting ? 0.5 : 1,
                                }}
                                onClick={exporting ? undefined : handleExport}
                            />
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>


            <Box display="flex" gap={1.75} flexWrap="wrap" alignItems="center" px={2} py={1}>
                {(["filed", "miss", "notDue", "blank", "off"] as const).map((k, idx) => {
                    const s = GST_STATUS_STYLE[k];
                    return (
                        <Box key={k} display="flex" alignItems="center" gap={1.75}>
                            {idx > 0 && <Typography sx={{ color: "text.disabled" }}>|</Typography>}
                            <Tooltip title={s.tooltip}>
                                <Typography sx={{ color: s.ink, cursor: "default" }}>{s.label}</Typography>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>

            <AppliedFilters />
            <Table
                sx={{ mt: 1 }}
                loading={isLoading || lodd}
                data={tableData || []}
                columns={columns}
                sortHandler={handleSort}
                selection={{ selected, setSelected }}
                pagination={{ totalCount, page, setPage, pageCount, setPageCount }}
                heading={RegularTaxHeadings.GSTR7}
            />
            <GstReturnsTracker open={openFilter} setOpen={setOpenFilter} />
        </Box>
    );
}

export default Gstr7Task;
